"use client"

import { Team } from "@/types/team"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { cosineDelete, cosinePost, cosinePut } from "./default-query"
import { User } from "@/types/user"
import { useCosineMutation } from "./mutation"
import { useAnalytics } from "@/hooks"
import { useRouter } from "next/navigation"
import { useMe } from "./user"

export const TeamKey = "team"
export const DetailKey = "detail"
export const ListKey = "list"

export const useTeam = (teamSlug: string) => {
  return useQuery<Team>({ queryKey: [TeamKey, DetailKey, `/teams/${teamSlug}`] })
}

export const useTeams = (initialData?: Team[]) => {
  return useQuery<Team[]>({ queryKey: [TeamKey, ListKey, `/teams`], initialData })
}

export const useCreateTeam = () => {
  const { track } = useAnalytics()
  const queryClient = useQueryClient()
  const user = useMe()
  return useCosineMutation<Team, { name: string; image: string }>({
    mutationFn: (data) => {
      const userId = user.data?.id

      if (!userId) {
        throw new Error("You must be logged in to create a team")
      }
      return cosinePost<Team>("/teams", { ...data, userId })
    },
    onSuccess: (data) => {
      track("team_created")
      queryClient.setQueriesData({ queryKey: [TeamKey, ListKey] }, (old?: Team[]) => [
        ...(old ?? []),
        data,
      ])
    },
  })
}

export const useTeamUsers = (teamId: string) => {
  return useQuery<User[]>({ queryKey: [TeamKey, DetailKey, `/teams/${teamId}/users`] })
}

export const useDeleteTeam = (teamId: string) => {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()
  return useCosineMutation<object, null>({
    mutationFn: () => cosineDelete(`/teams/${teamId}`),
    onSettled: () => {
      track("team_deleted")
    },
    onSuccess: () =>
      queryClient.setQueriesData({ queryKey: [TeamKey, ListKey] }, (old?: Team[]) =>
        old?.filter((p) => p.id !== teamId),
      ),
  })
}

type UpdateTeamPaylod = {
  name?: string
  image?: string
  slug?: string
  publicSshKey?: string
  privateSshKey?: string
}

export const useUpdateTeam = (teamId: string) => {
  const queryClient = useQueryClient()
  const router = useRouter()
  return useCosineMutation<Team, UpdateTeamPaylod>({
    mutationFn: (data) => cosinePut<Team>(`/teams/${teamId}`, data),
    onSuccess: (data) => {
      if (data.id !== teamId) {
        queryClient.clear()
        router.replace(`/${data.slug}/settings`)
      }
      queryClient.removeQueries({ queryKey: [TeamKey] })
      queryClient.setQueryData([TeamKey, DetailKey, `/teams/${data.slug}`], () => data)
      queryClient.setQueriesData({ queryKey: [TeamKey, ListKey] }, (old?: Team[]) =>
        old?.map((p) => (p.id === teamId ? data : p)),
      )
    },
  })
}

export const useInviteUserToTeam = (teamId: string) => {
  const queryClient = useQueryClient()
  const { track } = useAnalytics()
  return useCosineMutation<Team, { email: string; role: string }>({
    mutationFn: (data) => cosinePost<Team>(`/teams/${teamId}/invite`, data),
    onSuccess: () => {
      track("team_invite")
      queryClient.invalidateQueries({ queryKey: [TeamKey, DetailKey, `/teams/${teamId}/users`] })
    },
  })
}

export const useRemoveUserFromTeam = (teamId: string) => {
  const queryClient = useQueryClient()
  return useCosineMutation<{ userId: string }, { userId: string }>({
    mutationFn: ({ userId }) => cosineDelete(`/teams/${teamId}/users/${userId}`),
    onSuccess: (data) =>
      queryClient.setQueryData([TeamKey, DetailKey, `/teams/${teamId}/users`], (old: User[]) =>
        old?.filter((p) => p.id !== data.userId),
      ),
  })
}

export const useRemoveInviteFromTeam = (teamId: string) => {
  const queryClient = useQueryClient()
  return useCosineMutation<{ email: string }, string>({
    mutationFn: (email) => cosineDelete<{ email: string }>(`/teams/${teamId}/invite/${email}`),
    onSuccess: (data) =>
      queryClient.setQueryData([TeamKey, DetailKey, `/teams/${teamId}/users`], (old: User[]) =>
        old?.filter((p) => p.email !== data.email),
      ),
  })
}
